import { FC, useState } from 'react'
import styles from './PublicEndOrder.module.css'
import arrow from '../../assets/icons/IconArrowDownBlue.svg'
import { useAppSelector } from '../../redux/types/types'
import Select from 'react-select'
import { useMediaQuery } from 'react-responsive'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import * as XLSX from 'xlsx-js-style'
import robotoRegularBase64 from '../../assets/fonts/Roboto-Regular-base64'
import { addImageWithAspectRatio } from '../../utils/addImageWithAspectRatio'
import {
  Document,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  WidthType,
  AlignmentType,
} from 'docx'
import { saveAs } from 'file-saver'

const PublicEndOrder: FC = () => {
  const [openMore, setOpenMore] = useState(false)

  const { items: publicOrderItems, totalPrice } = useAppSelector((state) => state.publicOrder)
  const { selectedPlace, bookingData } = useAppSelector((state) => state.bookingTabs)

  const isMobile = useMediaQuery({ query: '(max-width: 767px)' })

  const formatDateRange = (startDate: string, endDate: string): string => {
    try {
      const start = new Date(startDate)
      const end = new Date(endDate)

      const formatDate = (date: Date): string => {
        const day = date.getDate().toString().padStart(2, '0')
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const year = date.getFullYear()
        const hours = date.getHours().toString().padStart(2, '0')
        const minutes = date.getMinutes().toString().padStart(2, '0')

        return `${day}.${month}.${year}, ${hours}:${minutes}`
      }

      return `${formatDate(start)} - ${formatDate(end)}`
    } catch (error) {
      console.error('Ошибка форматирования даты:', error)
      return `${startDate} - ${endDate}`
    }
  }

  const generatePDFContent = (doc: jsPDF) => {
    doc.setFont('Roboto', 'normal')
    doc.setFontSize(18)
    doc.text('Смета заказа', 14, 20)

    doc.setFontSize(12)
    doc.text(`Дата создания: ${new Date().toLocaleDateString()}`, 14, 30)
    doc.text(
      `Дата проведения: ${
        bookingData?.startDate && bookingData?.endDate
          ? formatDateRange(bookingData.startDate, bookingData.endDate)
          : `${bookingData?.startDate || ''} - ${bookingData?.endDate || ''}`
      }`,
      14,
      40,
    )
    doc.text(`Количество гостей: ${bookingData?.peopleCount}`, 14, 50)
    doc.text(`Название зала: ${selectedPlace.name}`, 14, 60)

    const tableBody = publicOrderItems.map((item) => [
      item.name,
      item.price.toString() + ' руб.',
      '1 шт.',
    ])

    autoTable(doc, {
      head: [['Имя', 'Цена', 'Количество']],
      body: tableBody,
      startY: 80,
      theme: 'grid',
      styles: {
        font: 'Roboto',
        fontSize: 12,
      },
      headStyles: {
        font: 'Roboto',
        fillColor: [209, 209, 209],
        textColor: 0,
        fontSize: 12,
        fontStyle: 'normal',
      },
      willDrawCell: (data) => {
        if (data.section === 'head') {
          doc.setFont('Roboto', 'normal')
        }
      },
    })

    // @ts-ignore
    const finalY = (doc as any).lastAutoTable?.finalY || 100

    doc.setFontSize(14)
    doc.text('Итоговая сумма: ' + totalPrice + ' руб.', 14, finalY + 15)
  }

  const createPDFWithoutLogo = (doc: jsPDF) => {
    generatePDFContent(doc)
    doc.save('Смета бронирования.pdf')
  }

  const handleDownloadPDF = async () => {
    const hasLogo = selectedPlace.establishment?.logo

    if (!hasLogo) {
      const doc = new jsPDF()
      doc.addFileToVFS('Roboto-Regular.ttf', robotoRegularBase64)
      doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal')
      doc.setFont('Roboto', 'normal')
      createPDFWithoutLogo(doc)
      return
    }

    downloadPDFWithLogo()
  }

  const downloadPDFWithLogo = async () => {
    const doc = new jsPDF()

    doc.addFileToVFS('Roboto-Regular.ttf', robotoRegularBase64)
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal')
    doc.setFont('Roboto', 'normal')

    try {
      const logoUrl = `${process.env.REACT_APP_BASE_URL_IMAGES}${selectedPlace.establishment?.logo}`
      const response = await fetch(logoUrl)

      if (!response.ok) {
        throw new Error('Не удалось загрузить логотип')
      }

      const blob = await response.blob()
      const imgData = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })

      await addImageWithAspectRatio(doc, imgData, 150, 10, 40, 40, 'JPEG')

      generatePDFContent(doc)
      doc.save('Смета бронирования.pdf')
    } catch (error) {
      console.error('Ошибка загрузки логотипа:', error)

      generatePDFContent(doc)
      doc.save('Смета бронирования.pdf')
    }
  }

  const downloadExcel = () => {
    const metadata = [
      {
        Имя: 'Дата создания',
        Цена: new Date().toLocaleDateString(),
        Количество: '',
      },
      {
        Имя: 'Дата проведения',
        Цена:
          bookingData?.startDate && bookingData?.endDate
            ? formatDateRange(bookingData.startDate, bookingData.endDate)
            : `${bookingData?.startDate || ''} - ${bookingData?.endDate || ''}`,
        Количество: '',
      },
      { Имя: 'Количество гостей', Цена: bookingData?.peopleCount, Количество: '' },
      { Имя: 'Название зала', Цена: selectedPlace.name, Количество: '' },
      { Имя: '', Цена: '', Количество: '' },
      { Имя: 'Состав заказа', Цена: 'Цена', Количество: 'Количество' },
    ]

    const orderData = [
      ...publicOrderItems.map((item) => ({
        Имя: item.name,
        Цена: item.price + ' руб.',
        Количество: '1',
      })),
      { Имя: 'Итоговая сумма', Цена: totalPrice + ' руб', Количество: '' },
    ]

    const dataForExcel = [...metadata, ...orderData]

    const worksheet = XLSX.utils.json_to_sheet(dataForExcel, { skipHeader: true })
    worksheet['!cols'] = [{ wch: 25 }, { wch: 40 }, { wch: 15 }]

    const boldRows = [0, 1, 2, 3, metadata.length - 1, metadata.length + publicOrderItems.length]

    const borderStyle = {
      top: { style: 'thin', color: { rgb: '000000' } },
      left: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      right: { style: 'thin', color: { rgb: '000000' } },
    }

    const range = XLSX.utils.decode_range(worksheet['!ref'] || '')
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C })
        if (!worksheet[cellRef]) continue

        if (C === 0 && boldRows.includes(R)) {
          worksheet[cellRef].s = {
            font: { bold: true },
            border: borderStyle,
          }
        } else {
          worksheet[cellRef].s = {
            border: borderStyle,
          }
        }
      }
    }

    const headerRowIndex = metadata.length - 1

    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C })
        if (!worksheet[cellRef]) continue

        if (boldRows.includes(R)) {
          if (R === headerRowIndex) {
            worksheet[cellRef].s = {
              font: { bold: true },
              border: borderStyle,
            }
          } else {
            if (C === 0) {
              worksheet[cellRef].s = {
                font: { bold: true },
                border: borderStyle,
              }
            } else {
              worksheet[cellRef].s = {
                border: borderStyle,
              }
            }
          }
        } else {
          worksheet[cellRef].s = {
            border: borderStyle,
          }
        }
      }
    }

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'PublicOrders')

    XLSX.writeFile(workbook, 'Смета бронирования.xlsx')
  }

  const downloadWord = async () => {
    const children: (Paragraph | Table)[] = []

    children.push(
      new Paragraph({
        text: 'Смета заказа',
        heading: 'Heading1',
        spacing: { after: 300 },
        alignment: AlignmentType.CENTER,
      }),
    )

    children.push(
      new Paragraph({
        text: `Дата создания: ${new Date().toLocaleDateString()}`,
        spacing: { after: 100 },
      }),
    )
    children.push(
      new Paragraph({
        text: `Дата проведения: ${
          bookingData?.startDate && bookingData?.endDate
            ? formatDateRange(bookingData.startDate, bookingData.endDate)
            : `${bookingData?.startDate || ''} - ${bookingData?.endDate || ''}`
        }`,
        spacing: { after: 100 },
      }),
    )
    children.push(
      new Paragraph({
        text: `Количество гостей: ${bookingData?.peopleCount}`,
        spacing: { after: 100 },
      }),
    )
    children.push(
      new Paragraph({
        text: `Название зала: ${selectedPlace.name}`,
        spacing: { after: 200 },
      }),
    )

    children.push(
      new Paragraph({
        text: 'Состав заказа',
        heading: 'Heading2',
        spacing: { before: 200, after: 100 },
      }),
    )

    const orderTableRows = [
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                text: 'Имя',
                run: { bold: true },
              }),
            ],
            width: { size: 50, type: WidthType.PERCENTAGE },
          }),
          new TableCell({
            children: [
              new Paragraph({
                text: 'Цена',
                run: { bold: true },
                alignment: AlignmentType.RIGHT,
              }),
            ],
            width: { size: 25, type: WidthType.PERCENTAGE },
          }),
          new TableCell({
            children: [
              new Paragraph({
                text: 'Количество',
                run: { bold: true },
                alignment: AlignmentType.CENTER,
              }),
            ],
            width: { size: 25, type: WidthType.PERCENTAGE },
          }),
        ],
      }),
      ...publicOrderItems.map(
        (item) =>
          new TableRow({
            children: [
              new TableCell({
                children: [new Paragraph(item.name)],
              }),
              new TableCell({
                children: [
                  new Paragraph({
                    text: `${item.price} руб.`,
                    alignment: AlignmentType.RIGHT,
                  }),
                ],
              }),
              new TableCell({
                children: [
                  new Paragraph({
                    text: '1 шт.',
                    alignment: AlignmentType.CENTER,
                  }),
                ],
              }),
            ],
          }),
      ),
    ]

    children.push(
      new Table({
        rows: orderTableRows,
        width: { size: 100, type: WidthType.PERCENTAGE },
      }),
    )

    children.push(
      new Paragraph({
        text: `Итоговая сумма: ${totalPrice} руб.`,
        run: { bold: true },
        spacing: { before: 300, after: 100 },
        heading: 'Heading2',
      }),
    )

    const doc = new Document({
      sections: [
        {
          children,
        },
      ],
    })

    Packer.toBlob(doc).then((blob: Blob) => {
      saveAs(blob, 'Смета бронирования.docx')
    })
  }

  if (publicOrderItems.length === 0) {
    return null
  }

  return (
    <div className={styles.OrderedDishesListCard}>
      <div className={styles.formContainer}>
        <h1 className={styles.title}>Бронирование</h1>
        <div className={styles.content}>
          <div className={styles.selectedPlace}>
            <img
              className={styles.selectedPlaceImage}
              src={`${process.env.REACT_APP_BASE_URL_IMAGES}${selectedPlace.images[0]}`}
              alt={''}
            />
            <div className={styles.selectedPlaceInfo}>
              <h2 className={styles.selectedPlaceName}>{selectedPlace.name}</h2>
              <h2 className={styles.selectedPlacePeopleCount}>
                {bookingData?.peopleCount} человек
              </h2>
              <span className={styles.selectedPlaceDate}>
                {bookingData?.startDate && bookingData?.endDate
                  ? formatDateRange(bookingData.startDate, bookingData.endDate)
                  : `${bookingData?.startDate || ''} - ${bookingData?.endDate || ''}`}
              </span>
            </div>
          </div>
          <div className={styles.dopServices}> Дополнительные услуги</div>
          {publicOrderItems.map((item) => (
            <div key={item.productId} className={styles.dishCard}>
              <div className={styles.nameContainer}>
                <div className={styles.left}>
                  <h2>{item?.name}</h2>
                  <span className={styles.price}>{item?.price} руб</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {publicOrderItems.length > 0 && (
          <div className={styles.orderInfoContainer}>
            <div className={styles.totalPriceContainer}>
              <span className={styles.totalPriceTitle}>Итоговая сумма</span>
              <span className={styles.totalPrice}>{totalPrice} руб</span>
            </div>
            <div className={styles.moreContainer}>
              <div onClick={() => setOpenMore(!openMore)} className={styles.moreWrapper}>
                <img src={arrow} />
                <span className={styles.moreTitle}>{openMore ? 'Свернуть' : 'Подробнее'}</span>
              </div>
              {openMore && (
                <>
                  <div className={styles.moreItemInfo}>
                    <span className={styles.leftSide}>Количество товаров</span>
                    <span className={styles.rightSide}>{publicOrderItems.length} шт</span>
                  </div>
                  <div className={styles.moreItemInfo}>
                    <span className={styles.leftSide}>Сумма заказа</span>
                    <span className={styles.rightSide}>{totalPrice} руб</span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        <div className={styles.saveButtonBlock}>
          <Select
            className={styles.reactSelect}
            classNamePrefix='react-select'
            value={null}
            options={[
              {
                value: 'download-pdf',
                label: 'Загрузить PDF',
                action: handleDownloadPDF,
              },
              {
                value: 'download-excel',
                label: 'Загрузить Excel',
                action: downloadExcel,
              },
              {
                value: 'download-word',
                label: 'Загрузить Word',
                action: downloadWord,
              },
            ]}
            menuPlacement={isMobile ? 'top' : 'bottom'}
            onChange={(selectedOption) => {
              if (selectedOption && selectedOption.action) {
                selectedOption.action()
              }
            }}
            name='export-actions'
            isClearable={false}
            placeholder='Загрузить смету'
            noOptionsMessage={() => 'Нет вариантов экспорта'}
            styles={{
              control: (provided) => ({
                ...provided,
                border: 'none',
                boxShadow: 'none',
                minHeight: 28,

                backgroundColor: 'transparent',
              }),
              menu: (provided) => ({
                ...provided,
                zIndex: 5,
                right: 0,
              }),
              input: (provided) => ({
                ...provided,
                margin: 0,
                padding: 0,
                fontSize: isMobile ? 13 : 11,
                height: isMobile ? 42 : 24,
              }),
              placeholder: (provided) => ({ ...provided, color: 'black' }),
              valueContainer: (provided) => ({
                ...provided,
                padding: 0,
              }),
              dropdownIndicator: (provided) => ({
                ...provided,
                padding: 0,
              }),
              clearIndicator: (provided) => ({
                ...provided,
                padding: 0,
              }),
              indicatorSeparator: () => ({
                display: 'none',
              }),
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default PublicEndOrder
